import moment from 'moment';
import { filterByMarket, dataFilter } from 'modules/Prices/helpers';
import { MarketOption } from 'interface';

const transformDataForChart = ({
  data,
  currency,
  network,
  market
}: {
  data: any[];
  currency: string;
  network: string | null;
  market: MarketOption | null;
}) => {
  const isYearDate = !market && network && ['ON', 'OFF'].includes(network);

  // Date formatter function
  const formatName = (date: string) => {
    return isYearDate
      ? moment(date).format('YYYY')
      : moment(date).format('MM/YYYY');
  };

  // Sort function
  const getSortFunction = (a: any, b: any) =>
    moment(a, 'YYYY-MM-DD').diff(moment(b, 'YYYY-MM-DD'));

  // Transform item function
  const transformItem = (item: any) => {
    const useLocalPrice = currency && currency !== item.displayed_currency;
    return {
      ...item,
      name: formatName(item.sc_date),
      currency: useLocalPrice ? item.local_currency : item.displayed_currency,
      value:
        Math.round(
          100 * Number(useLocalPrice ? item.avg_local_price : item.avg_price)
        ) / 100
    };
  };

  // Create formatted data with dates as names (x axis) and prices as values (y axis)
  let formattedData = data.map((set) =>
    set.map((item: any) => transformItem(item))
  );

  // Sort formatted data by names (dates)
  formattedData.forEach((set) =>
    set.sort((a: any, b: any) => getSortFunction(a.sc_date, b.sc_date))
  );

  // Create unique sorted list of names (dates)
  const setNames = [
    ...new Set(
      formattedData.flatMap((set) => set.map((item: any) => item.name))
    )
  ].sort(getSortFunction);

  // Compute average value for same names (dates)
  formattedData = formattedData.map((set) => {
    return setNames.map((name) => {
      const items = set.filter((item: any) => item.name === name);

      if (items.length === 0) {
        return {
          name,
          value: null
        };
      }

      const sum = items
        .map((item: any) => item.value * item.facing)
        .reduce((a: any, b: any) => a + b, 0);
      const facing = items
        .map((item: any) => item.facing)
        .reduce((a: any, b: any) => a + b, 0);

      const useLocalPrice =
        currency && currency !== items[0].displayed_currency;
      return {
        name: name,
        currency: useLocalPrice
          ? items[0].local_currency
          : items[0].displayed_currency,
        value: Math.round(100 * (sum / facing)) / 100,
        wine_name: items[0].wine_name,
        vintage: items[0].vintage
      };
    });
  });

  // Fill missing values (prices) with null values
  formattedData.forEach((set) => {
    setNames.forEach((name, index) => {
      if (!set[index] || set[index].name !== name) {
        set.splice(index, 0, {
          name,
          value: null
        });
      }
    });
  });

  return formattedData;
};

const getDataFilters = ({ network }: { network: string | null }) => [
  { value: network, key: 'network_code', default: [] }
];

const applyFiltersToData = ({
  data,
  network,
  market
}: {
  data: any[];
  network: string;
  market: MarketOption | null;
}) => {
  let filteredData = dataFilter(data, getDataFilters({ network }));
  filteredData = filterByMarket(filteredData, market, 'sc_date');
  return filteredData;
};

export { getDataFilters, transformDataForChart, applyFiltersToData };
